/* Read a compact stock index for global grade counts/order; fetch row HTML only
   for the source pages needed by each visible batch. Search keeps its own flow. */
(function () {
  'use strict';
  var size = 24;

  function rowLoader(base, fetchPage) {
    var cache = new Map();
    async function read(page) {
      if (!Number.isInteger(page) || page < 1) throw new Error('Invalid stock page');
      if (!cache.has(page)) {
        var url = new URL(base);
        url.searchParams.set('view', 'stock-rows');
        url.searchParams.set('page', page);
        var pending = (async function () {
          var response = await fetchPage(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
          if (!response.ok) throw new Error('Unable to load products');
          var data = await response.json();
          if (data.error || !Array.isArray(data.products)) throw new Error('Unable to load products');
          return data.products;
        }());
        cache.set(page, pending);
        pending.catch(function () { cache.delete(page); });
      }
      return cache.get(page);
    }
    return async function (items) {
      var pages = Array.from(new Set(items.map(function (p) { return p.sourcePage; })));
      var rows = new Map(), cursor = 0;
      // Bound request concurrency when selected-grade prices reorder source pages.
      await Promise.all(Array.from({ length: Math.min(4, pages.length) }, async function () {
        while (cursor < pages.length) {
          var products = await read(pages[cursor++]);
          products.forEach(function (p) { rows.set(p.id, p); });
        }
      }));
      return items.map(function (item) {
        var row = rows.get(item.id);
        if (!row || typeof row.html !== 'string' || !Array.isArray(row.variants)) {
          cache.delete(item.sourcePage);
          throw new Error('Stock changed. Refresh the page to see the latest products.');
        }
        // Never combine an old stock/price index with a changed buying form.
        if (JSON.stringify(row.variants) !== JSON.stringify(item.allVariants)) {
          cache.delete(item.sourcePage);
          throw new Error('Stock changed. Refresh the page to see the latest products.');
        }
        return Object.assign({}, item, { html: row.html });
      });
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { rowLoader: rowLoader };
    return;
  }
  var root = document.querySelector('[data-stock-lazy]');
  var stock = window.HciCollectionStock;
  if (!root || !stock) return;
  var current = new URL(window.location.href);
  var selected = current.searchParams.getAll('filter.v.option.condition').flatMap(function (v) { return v.split(','); });
  var sort = root.querySelector('select[name="sort_by"]');
  var activeSort = current.searchParams.get('sort_by') || (sort && sort.value);
  var url = new URL(root.dataset.stockCollection, current.origin);
  url.search = current.search;
  url.searchParams.delete('filter.v.option.condition');
  url.searchParams.set('view', 'stock-index');
  if (activeSort) url.searchParams.set('sort_by', activeSort);
  var status = root.querySelector('[data-stock-status]');
  var results = root.querySelector('[data-stock-results]');
  var range = root.querySelector('[data-stock-result-range]');
  var fetchRows = rowLoader(url, window.fetch.bind(window));
  var list, more, observer, items, start, offset, busy = false, initialised = false, needsRefresh = false;
  var stateKey = current.pathname + current.search;
  var remembered = window.history.state && window.history.state.hciCollection;
  var restoreTo = remembered && remembered.key === stateKey ? remembered.end : 0;
  var restoreY = remembered && remembered.key === stateKey ? remembered.y : 0;

  function remember() {
    if (!initialised) return;
    try {
      window.history.replaceState(Object.assign({}, window.history.state, {
        hciCollection: { key: stateKey, end: offset, y: window.scrollY }
      }), '');
    } catch (_) { /* Browsing still works when history storage is unavailable. */ }
  }
  window.addEventListener('pagehide', remember);
  window.addEventListener('pageshow', function () { if (observer && more && !more.hidden) observer.observe(more); });
  // Save before following a product link, including when bfcache is unavailable.
  results.addEventListener('click', function (event) {
    if (event.target.closest('a[href]')) remember();
  });

  function render(product) {
    var template = document.createElement('template');
    template.innerHTML = product.html;
    var row = template.content.querySelector('.prow');
    if (!row) throw new Error('Unable to load products');
    if (product.pushed) row.classList.add('prow--push');
    var quantity = row.querySelector('.prow__stock');
    if (quantity) quantity.textContent = product.variants.reduce(function (n, v) { return n + v.quantity; }, 0) + ' in stock';
    var grades = row.querySelector('[data-stock-grades]');
    if (grades) grades.textContent = stock.gradeSummary(product.variants);
    var allowed = product.variants.map(function (v) { return String(v.id); });
    row.querySelectorAll('[data-row-cart]').forEach(function (form) {
      if (!allowed.includes(form.dataset.variant)) form.remove();
    });
    var offers = Array.from(row.querySelectorAll('[data-row-cart]'));
    var price = row.querySelector('[data-row-price]');
    if (price && offers.length) {
      var cheapest = offers.reduce(function (a, b) { return Number(a.dataset.price) <= Number(b.dataset.price) ? a : b; });
      price.textContent = (offers.length > 1 ? 'From ' : '') + cheapest.dataset.priceText;
    }
    if (selected.length) row.querySelectorAll('a[href]').forEach(function (link) {
      var target = new URL(link.getAttribute('href'), current.origin);
      if (target.pathname.includes('/products/')) {
        target.searchParams.set('variant', product.variants[0].id);
        link.href = target.toString();
      }
    });
    return template.content;
  }

  async function append() {
    if (busy || offset >= items.length) return;
    busy = true;
    more.disabled = true;
    more.textContent = 'Loading products...';
    results.setAttribute('aria-busy', 'true');
    try {
      var batch = await fetchRows(items.slice(offset, offset + size));
      var fragment = document.createDocumentFragment();
      batch.forEach(function (product) { fragment.appendChild(render(product)); });
      if (!initialised) {
        results.replaceChildren(list, more);
        initialised = true;
      }
      list.appendChild(fragment);
      offset += batch.length;
      range.textContent = (start + 1) + '\u2013' + offset + ' of ' + items.length;
      results.hidden = false;
      status.hidden = false;
      status.textContent = offset === items.length ? 'All ' + (offset - start) + ' products loaded.' : '';
      more.hidden = offset >= items.length;
      // The theme's .btn display rule overrides the browser's [hidden] default.
      more.style.display = more.hidden ? 'none' : '';
      more.textContent = 'Load more products';
      if (observer) {
        observer.unobserve(more);
        if (!more.hidden) observer.observe(more);
      }
      return true;
    } catch (error) {
      status.hidden = false;
      needsRefresh = error.message.indexOf('Stock changed') === 0;
      status.textContent = needsRefresh ? error.message : 'Unable to load more products. Please try again.';
      if (!initialised) {
        // Do not expose an unverified grade/price result after initial failure.
        results.replaceChildren(more);
        results.hidden = false;
      }
      more.textContent = needsRefresh ? 'Refresh products' : 'Try again';
      if (observer) observer.unobserve(more);
      return false;
    } finally {
      busy = false;
      more.disabled = false;
      results.setAttribute('aria-busy', 'false');
    }
  }

  stock.loadProducts(url, window.fetch.bind(window)).then(async function (products) {
    var counts = stock.counts(products);
    document.querySelectorAll('[data-stock-condition]').forEach(function (label) {
      var input = label.querySelector('input');
      var count = counts.get(input.value) || 0;
      label.querySelector('.facet__opt-count').textContent = count;
      input.disabled = count === 0 && !input.checked;
      label.hidden = count === 0;
      label.style.display = count === 0 ? 'none' : '';
    });
    products = products.map(function (p) { return Object.assign({}, p, { allVariants: p.variants }); });
    items = stock.ordered(stock.matches(products, selected), activeSort);
    var page = Math.min(Math.max(parseInt(current.searchParams.get('page'), 10) || 1, 1), Math.ceil(items.length / size) || 1);
    start = offset = (page - 1) * size;
    document.querySelectorAll('[data-stock-result-total]').forEach(function (node) {
      node.textContent = items.length + (items.length === 1 ? ' product' : ' products');
    });
    if (!items.length) {
      var empty = document.createElement('div');
      empty.className = 'col-empty';
      var heading = document.createElement('h3');
      heading.textContent = 'Nothing matches those filters right now.';
      var clear = document.createElement('a');
      clear.className = 'btn btn--ink';
      clear.href = root.dataset.stockCollection;
      clear.textContent = 'Clear filters';
      empty.append(heading, clear);
      results.replaceChildren(empty);
      results.hidden = false;
      range.textContent = '0 products';
      status.hidden = true;
      return;
    }
    list = document.createElement('div');
    list.className = 'deals-list';
    more = document.createElement('button');
    more.type = 'button';
    more.className = 'btn btn--ink';
    more.style.cssText = 'margin:24px auto';
    more.setAttribute('data-load-more', '');
    more.addEventListener('click', function () {
      if (needsRefresh) window.location.reload();
      else append();
    });
    // Restore previously loaded rows on Back/reload, not the entire catalogue.
    var loaded = await append();
    while (loaded && offset < Math.min(restoreTo, items.length)) loaded = await append();
    if (loaded && restoreY) window.scrollTo(0, restoreY);
    if ('IntersectionObserver' in window) {
      observer = new window.IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) append();
      }, { rootMargin: '400px 0px' });
      if (loaded && !more.hidden) observer.observe(more);
    }
  }).catch(function () {
    status.hidden = false;
    status.textContent = 'Unable to check stock by condition. Please refresh to try again.';
    // The initial Liquid fallback remains safe only without grade/price sorting.
    if (selected.length || (activeSort && activeSort.indexOf('price-') === 0)) results.hidden = true;
  });
}());
